/**
 * The soundtrack (WebAudio, no assets): a generated ambient score that follows the match. Six
 * stages on the match clock — lobby, watch (0:00), weapons free (4:00: a distant siren, E♭ rubs
 * against the D, a slow heartbeat), capitals exposed (10:00: tritone, choir, glass), final (17:00:
 * stripped back, an endless rise) and aftermath (a hard cut, then wind and an unresolved chord) —
 * and what happens to us: a warhead coming down on our things (the music ducks and muffles, a
 * throb comes in), a hit at home (a cut and ringing ears), each city lost (one note of the piano
 * phrase gone for good), our capital critical (the heartbeat loses its rhythm, the drone sags
 * flat) and being knocked out (wind). Shares the effects' AudioContext: silent with SOUND off, and
 * has its own MUSIC button, which cycles COLD WAR (this score, the default) → NIGHT SHIFT (lofi,
 * `nightshift.ts`, fed the same cue and events) → OFF; the graph only exists while it plays. Mix
 * tuned by ear in the score lab (an artifact page): levels, sends and timings here match it.
 */
import { NightShift } from './nightshift';
import type { Sfx } from './sfx';

const MUSIC_KEY = 'sa.music';
const TRACK_KEY = 'sa.track';
export type Track = 'coldwar' | 'nightshift';
const TRACK_NAME: Record<Track, string> = { coldwar: 'COLD WAR', nightshift: 'NIGHT SHIFT' };
/** output level: the score lab's balance against the effects (2 : 1; the effects' master is 0.5) */
const LEVEL = 1;
/** the control loop (ms) and how far ahead it schedules notes and beats (s) */
const TICK_MS = 50;
const AHEAD_S = 0.25;

export const STAGE = { lobby: 0, watch: 1, release: 2, exposed: 3, final: 4, aftermath: 5 } as const;
export type MusicStage = (typeof STAGE)[keyof typeof STAGE];

export interface MusicCue {
  stage: MusicStage;
  /** 0..1: heartbeat rate and level, the minor second, Geiger rate, drone brightness */
  tension: number;
  /** a warhead is coming down on something of ours: duck + muffle, the throb */
  inbound: boolean;
  /** our capital is critical (§1.11): the arrhythmic heartbeat */
  critical: boolean;
  /** knocked out, spectating: wind */
  eliminated: boolean;
}

export const LOBBY_CUE: MusicCue = { stage: STAGE.lobby, tension: 0.1, inbound: false, critical: false, eliminated: false };

/** level per stage (0..1, by `MusicStage`) × `base`; `send`: reverb; `lv` null: driven by events */
interface LayerDef {
  base: number;
  send: number;
  lv: readonly number[] | null;
}
const LAYERS = {
  drone: { base: 0.32, send: 0.25, lv: [0.55, 0.8, 0.9, 1, 0.95, 0] },
  fifth: { base: 0.11, send: 0.55, lv: [0.45, 0.8, 0.7, 0.45, 0, 0] },
  minor2: { base: 0.075, send: 0.6, lv: [0, 0, 0.7, 0.8, 0.35, 0] },
  tritone: { base: 0.065, send: 0.6, lv: [0, 0, 0, 0.75, 0.3, 0] },
  piano: { base: 0.2, send: 0.85, lv: [0.4, 1, 0.9, 0.8, 0.55, 0] },
  pulse: { base: 0.5, send: 0.12, lv: [0, 0, 0.55, 0.75, 1, 0] },
  choir: { base: 0.06, send: 0.85, lv: [0, 0, 0.2, 0.8, 0.45, 0] },
  shimmer: { base: 0.03, send: 0.95, lv: [0, 0, 0, 0.75, 0.4, 0] },
  shepard: { base: 0.06, send: 0.6, lv: [0, 0, 0, 0, 1, 0] },
  radio: { base: 0.06, send: 0.45, lv: [1, 0.6, 0.4, 0.3, 0.1, 0] },
  geiger: { base: 0.14, send: 0.1, lv: [0, 0, 0.6, 0.8, 1, 0.5] },
  wind: { base: 0.12, send: 0.3, lv: [0.35, 0, 0, 0, 0, 1] },
  requiem: { base: 0.08, send: 0.8, lv: [0, 0, 0, 0, 0, 1] },
  siren: { base: 0.07, send: 1, lv: null },
  throb: { base: 0.3, send: 0.1, lv: null },
} satisfies Record<string, LayerDef>;
type LayerId = keyof typeof LAYERS;

/** the piano phrase (MIDI): one note per city we can lose; darker from weapons free / capitals exposed */
const MOTIF = [74, 69, 65, 64, 62, 60, 57];
/** which note each lost city takes, in order */
const LOST_ORDER = [3, 1, 5, 0, 6, 2, 4];
const motifAt = (stage: number): number[] => MOTIF.map((m, i) => (stage >= 2 && i === 3 ? 63 : stage >= 3 && i === 1 ? 68 : m));
/** phrase gaps by stage (s, watch … final) */
const PHRASE_GAP: readonly (readonly [number, number])[] = [[0, 0], [12, 22], [10, 18], [9, 16], [20, 30]];
/** heartbeat rate by stage (bpm, weapons free … final) */
const BPM = [46, 54, 60];
/** Geiger clicks per second by stage */
const GEIGER = [0, 0, 0.6, 1.5, 4, 1];
const MORSE: Record<string, string> = { A: '.-', C: '-.-.', D: '-..', E: '.', K: '-.-', N: '-.', Q: '--.-', R: '.-.', S: '...', T: '-', V: '...-', Z: '--..', 0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-', 5: '.....', 7: '--...', 9: '----.' };
const MORSE_TEXT = ['VVV', 'CQ CQ', 'QRV', 'ZULU 4', '73914', '50207', 'K', 'NDT', 'ZZ 1'];
/** "ah" ↔ "oo" choir formants (Hz) */
const FORMANTS = [[730, 300], [1090, 870], [2440, 2240]];
/** a distant hit ducks the music at most this often (s): a salvo is one dip, not a stutter */
const FAR_HIT_EVERY_S = 3;

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12);
const rand = (a: number, b: number) => a + Math.random() * (b - a);

function loadOn(): boolean {
  try {
    return localStorage.getItem(MUSIC_KEY) !== '0';
  } catch {
    return true;
  }
}

function loadTrack(): Track {
  try {
    return localStorage.getItem(TRACK_KEY) === 'nightshift' ? 'nightshift' : 'coldwar';
  } catch {
    return 'coldwar';
  }
}

interface Layer {
  in: GainNode;
  fader: GainNode;
  /** the fader's current target (only a moved target is re-scheduled) */
  last: number;
}

/** Everything that exists while the music plays. */
interface Graph {
  ctx: AudioContext;
  /** level → limiter → speakers */
  post: GainNode;
  /** ducking (inbound, hits, the aftermath cut) and the inbound / hit muffle */
  duck: GainNode;
  muffle: BiquadFilterNode;
  dry: GainNode;
  reverb: GainNode;
  layers: Record<LayerId, Layer>;
  noise: AudioBuffer;
  radioBp: BiquadFilterNode;
  droneLp: BiquadFilterNode;
  drone: { o: OscillatorNode; detune: number }[];
  formants: BiquadFilterNode[][];
  shepard: { o: OscillatorNode; g: GainNode; last: number }[];
  /** persistent sources, stopped on teardown */
  sources: AudioScheduledSourceNode[];
}

export class Music {
  on = loadOn();
  track = loadTrack();
  /** COLD WAR's graph, while it plays */
  private g: Graph | null = null;
  /** NIGHT SHIFT, while it plays */
  private lofi: NightShift | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private cue: MusicCue = LOBBY_CUE;
  /** cities lost this match: notes gone from the phrase */
  private lost = 0;
  /** next phrase / lobby note / beat / click / crackle / morse (AudioContext time) */
  private next = { phrase: 0, single: 0, beat: 0, geiger: 0, crackle: 0, morse: 0 };
  /** the next beat after a dropped one lands hard (arrhythmia) */
  private strongBeat = false;
  private lastFarHit = -Infinity;

  constructor(private readonly sfx: Sfx) {
    sfx.onChange = () => this.sync();
  }

  /** The MUSIC button's words: `MUSIC · COLD WAR`, `MUSIC · NIGHT SHIFT` or `MUSIC · OFF`. */
  get label(): string {
    return `MUSIC · ${this.on ? TRACK_NAME[this.track] : 'OFF'}`;
  }

  /** The MUSIC button: COLD WAR → NIGHT SHIFT → OFF → COLD WAR. Returns the new label. */
  cycle(): string {
    if (!this.on) [this.on, this.track] = [true, 'coldwar'];
    else if (this.track === 'coldwar') this.track = 'nightshift';
    else this.on = false;
    try {
      localStorage.setItem(MUSIC_KEY, this.on ? '1' : '0');
      localStorage.setItem(TRACK_KEY, this.track);
    } catch {
      /* storage blocked: session-only */
    }
    // a new track starts fresh (from where the match stands)
    this.teardown();
    this.sync();
    return this.label;
  }

  get citiesLost(): number {
    return this.lost;
  }

  /** A new match: every city back in the phrase. */
  newMatch(): void {
    this.lost = 0;
    this.lofi?.newMatch();
  }

  /** One of our cities is gone: its note leaves the phrase (and a low bell tolls). */
  cityLost(): void {
    if (this.lost >= MOTIF.length) return;
    this.lost++;
    this.lofi?.cityLost();
    const g = this.g;
    if (g) this.pianoNote(g, g.ctx.currentTime + 0.05, 38, 0.9);
  }

  /** A detonation: at home the music cuts out and the ears ring; elsewhere a dip and a far rumble. */
  hit(near: boolean): void {
    this.lofi?.hit(near);
    const g = this.g;
    if (!g) return;
    const t = g.ctx.currentTime;
    if (near) {
      g.duck.gain.cancelScheduledValues(t);
      g.duck.gain.setValueAtTime(0, t);
      g.duck.gain.setTargetAtTime(this.cue.inbound ? 0.55 : 1, t + 1.4, 2.8);
      g.muffle.frequency.cancelScheduledValues(t);
      g.muffle.frequency.setValueAtTime(260, t);
      g.muffle.frequency.setTargetAtTime(this.cue.inbound ? 650 : 18000, t + 2, 4);
      for (const [f, v] of [[4186, 0.03], [4203, 0.02]]) {
        const o = osc(g.ctx, 'sine', f);
        const env = gain(g.ctx, 0.0001);
        env.gain.setValueAtTime(0.0001, t + 0.3);
        env.gain.exponentialRampToValueAtTime(v, t + 0.6);
        env.gain.exponentialRampToValueAtTime(0.0001, t + 8);
        o.connect(env).connect(g.post);
        o.start(t);
        o.stop(t + 8.1);
      }
      return;
    }
    if (t - this.lastFarHit < FAR_HIT_EVERY_S) return;
    this.lastFarHit = t;
    const s = g.ctx.createBufferSource();
    s.buffer = g.noise;
    s.loop = true;
    const env = gain(g.ctx, 0.0001);
    env.gain.setValueAtTime(0.0001, t + 0.4);
    env.gain.exponentialRampToValueAtTime(0.5, t + 0.9);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 5);
    s.connect(filt(g.ctx, 'lowpass', 120)).connect(env).connect(g.post);
    s.start(t);
    s.stop(t + 5.1);
    if (!this.cue.inbound) {
      g.duck.gain.cancelScheduledValues(t);
      g.duck.gain.setValueAtTime(g.duck.gain.value, t);
      g.duck.gain.setTargetAtTime(0.72, t + 0.4, 0.2);
      g.duck.gain.setTargetAtTime(1, t + 1.6, 1.5);
    }
  }

  /** Where the match stands (every frame in a match; once per screen outside one). */
  set(cue: MusicCue): void {
    const prev = this.cue;
    this.cue = cue;
    if (!this.sync()) return;
    if (this.lofi) {
      this.lofi.set(cue);
      return;
    }
    const g = this.g!;
    const t = g.ctx.currentTime;
    if (cue.stage !== prev.stage) {
      // weapons free, as it happens (not when joining late): the siren, once
      if (prev.stage === STAGE.watch && cue.stage > STAGE.watch && cue.stage < STAGE.aftermath) this.siren(g);
      if (cue.stage === STAGE.aftermath) {
        g.duck.gain.cancelScheduledValues(t);
        g.duck.gain.setValueAtTime(0, t);
        g.duck.gain.setTargetAtTime(1, t + 2.4, 3);
      }
      this.next.phrase = t + rand(2, 5);
    }
    if (cue.inbound !== prev.inbound) {
      ramp(g.duck.gain, cue.inbound ? 0.55 : 1, cue.inbound ? 0.6 : 2, t);
      ramp(g.muffle.frequency, cue.inbound ? 650 : 18000, cue.inbound ? 0.6 : 2.5, t);
    }
    const crit = cue.critical && !cue.eliminated;
    if (crit !== (prev.critical && !prev.eliminated)) {
      // the drone sags flat while the capital hangs on
      for (const d of g.drone) ramp(d.o.detune, crit ? d.detune - 38 : d.detune, crit ? 4 : 5, t);
      if (crit) {
        this.next.beat = t + 0.15;
        this.next.phrase = t + rand(1, 2.5);
      }
    }
  }

  /** Build or tear down the graph: it plays while unlocked, MUSIC on and SOUND on. True when it plays. */
  sync(): boolean {
    const ctx = this.sfx.context;
    const play = !!ctx && this.on && !this.sfx.muted;
    if (play && !this.g && !this.lofi) {
      if (this.track === 'nightshift') this.lofi = new NightShift(ctx, ctx.destination, this.cue, this.lost);
      else this.build(ctx);
    } else if (!play) this.teardown();
    return play;
  }

  dispose(): void {
    this.teardown();
  }

  // ------------------------------------------------------------------ graph

  private build(ctx: AudioContext): void {
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 6;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.005;
    limiter.release.value = 0.3;
    const post = gain(ctx, 0);
    post.gain.setTargetAtTime(LEVEL, ctx.currentTime, 1.5);
    post.connect(limiter).connect(ctx.destination);
    const duck = gain(ctx, 1);
    const muffle = filt(ctx, 'lowpass', 18000, 0.5);
    duck.connect(muffle).connect(post);
    const dry = gain(ctx, 1);
    dry.connect(duck);
    const verb = ctx.createConvolver();
    verb.buffer = impulse(ctx, 6.5);
    const reverb = gain(ctx, 1);
    reverb.connect(verb).connect(gain(ctx, 0.9)).connect(duck);
    const layers = {} as Record<LayerId, Layer>;
    for (const id of Object.keys(LAYERS) as LayerId[]) {
      const def: LayerDef = LAYERS[id];
      const l: Layer = { in: gain(ctx, 1), fader: gain(ctx, def.lv ? 0 : def.base), last: -1 };
      l.in.connect(l.fader);
      l.fader.connect(dry);
      l.fader.connect(gain(ctx, def.send)).connect(reverb);
      layers[id] = l;
    }
    const g: Graph = {
      ctx,
      post,
      duck,
      muffle,
      dry,
      reverb,
      layers,
      noise: noise(ctx, 3),
      radioBp: filt(ctx, 'bandpass', 1700, 0.9),
      droneLp: filt(ctx, 'lowpass', 170, 0.8),
      drone: [],
      formants: [],
      shepard: [],
      sources: [],
    };
    this.g = g;
    this.voices(g);
    const t = ctx.currentTime;
    this.next = { phrase: t + rand(1, 3), single: t + rand(1, 3), beat: 0, geiger: 0, crackle: 0, morse: t + rand(8, 20) };
    // (re)built mid-match: start where the match stands
    if (this.cue.inbound) {
      duck.gain.value = 0.55;
      muffle.frequency.value = 650;
    }
    if (this.cue.critical && !this.cue.eliminated) for (const d of g.drone) d.o.detune.value = d.detune - 38;
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private teardown(): void {
    this.lofi?.dispose();
    this.lofi = null;
    const g = this.g;
    if (!g) return;
    this.g = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const t = g.ctx.currentTime;
    g.post.gain.cancelScheduledValues(t);
    g.post.gain.setTargetAtTime(0, t, 0.15);
    // after the fade: stop the sources, unhook the graph
    setTimeout(() => {
      for (const s of g.sources) s.stop();
      g.post.disconnect();
    }, 800);
  }

  /** The persistent voices; the layer faders open and close them. */
  private voices(g: Graph): void {
    const { ctx, layers: L } = g;
    const start = <T extends AudioScheduledSourceNode>(s: T): T => {
      s.start(ctx.currentTime + Math.random() * 0.1);
      g.sources.push(s);
      return s;
    };
    const lfo = (rate: number, depth: number, param: AudioParam) => {
      start(osc(ctx, 'sine', rate)).connect(gain(ctx, depth)).connect(param);
    };
    const loopNoise = () => {
      const s = ctx.createBufferSource();
      s.buffer = g.noise;
      s.loop = true;
      s.start(ctx.currentTime, Math.random() * 2);
      g.sources.push(s);
      return s;
    };
    const pad = (id: LayerId, notes: number[], cutoff: number, rate: number, spread: number) => {
      const lp = filt(ctx, 'lowpass', cutoff);
      const amp = gain(ctx, 0.6);
      lfo(rate, 0.4, amp.gain);
      for (const m of notes) for (const d of [-spread, spread]) start(osc(ctx, 'triangle', midi(m), d)).connect(gain(ctx, 0.5)).connect(lp);
      lp.connect(amp).connect(L[id].in);
    };

    // drone: D1 / D2 saws and sine subs through a breathing low-pass
    lfo(0.03, 60, g.droneLp.frequency);
    for (const [type, m, d, v] of [['sawtooth', 26, -6, 0.5], ['sawtooth', 38, 5, 0.35], ['sine', 26, 0, 0.7], ['sine', 38, 0, 0.25]] as const) {
      const o = start(osc(ctx, type, midi(m), d));
      o.connect(gain(ctx, v)).connect(g.droneLp);
      g.drone.push({ o, detune: d });
    }
    g.droneLp.connect(L.drone.in);

    pad('fifth', [45, 50, 57], 1100, 0.04, 4);
    pad('minor2', [51, 63], 900, 0.07, 6);
    pad('tritone', [44, 56], 850, 0.05, 7);
    pad('requiem', [38, 45, 53, 63], 700, 0.03, 5);
    start(osc(ctx, 'sine', midi(26))).connect(gain(ctx, 0.5)).connect(L.requiem.in);

    // choir: detuned saws through vowel formants that drift between "ah" and "oo"
    const choirOut = filt(ctx, 'lowpass', 2600);
    choirOut.connect(L.choir.in);
    for (const m of [50, 57, 63]) {
      const v = gain(ctx, 1);
      for (const d of [-7, 7]) {
        const o = start(osc(ctx, 'sawtooth', midi(m), d));
        lfo(4.6 + Math.random(), 7, o.detune);
        o.connect(v);
      }
      g.formants.push(
        [[730, 1], [1090, 0.5], [2440, 0.22]].map(([f, a]) => {
          const b = filt(ctx, 'bandpass', f, 9);
          v.connect(b).connect(gain(ctx, a)).connect(choirOut);
          return b;
        }),
      );
    }

    // glass: high sines with slow tremolo
    for (const m of [86, 93, 100, 98]) {
      const o = start(osc(ctx, 'sine', midi(m)));
      const amp = gain(ctx, 0.5);
      lfo(rand(0.05, 0.15), 0.5, amp.gain);
      lfo(0.02, 12, o.detune);
      o.connect(amp).connect(L.shimmer.in);
    }

    // the endless rise (Shepard–Risset): 7 sines, moved by the control loop
    for (let i = 0; i < 7; i++) {
      const o = start(osc(ctx, 'sine', 40));
      const amp = gain(ctx, 0);
      o.connect(amp).connect(L.shepard.in);
      g.shepard.push({ o, g: amp, last: 0 });
    }

    // radio: hiss, plus crackle bursts and morse scheduled into the band-pass
    loopNoise().connect(filt(ctx, 'highpass', 2600)).connect(gain(ctx, 0.08)).connect(L.radio.in);
    g.radioBp.connect(L.radio.in);

    // wind: two band-passed noise bands with gusts and a slow pan
    const pan = ctx.createStereoPanner();
    lfo(0.05, 0.6, pan.pan);
    pan.connect(L.wind.in);
    for (const [f, rate, q] of [[420, 0.07, 1.4], [220, 0.11, 0.9]]) {
      const bp = filt(ctx, 'bandpass', f, q);
      lfo(rate, f * 0.55, bp.frequency);
      const amp = gain(ctx, 0.6);
      lfo(rate * 1.7, 0.45, amp.gain);
      loopNoise().connect(bp).connect(amp).connect(pan);
    }

    // inbound throb: a sub sine with a 1.6 Hz wobble
    const am = gain(ctx, 0.5);
    lfo(1.6, 0.5, am.gain);
    for (const [f, v] of [[42, 0.9], [84, 0.25]]) start(osc(ctx, 'sine', f)).connect(gain(ctx, v)).connect(am);
    am.connect(L.throb.in);
  }

  // ------------------------------------------------------------------ control loop

  private tick(): void {
    const g = this.g;
    if (!g) return;
    const { ctx, layers } = g;
    const t = ctx.currentTime;
    const ahead = t + AHEAD_S;
    const c = this.cue;
    const st = c.stage;
    const crit = c.critical && !c.eliminated;
    const tension = Math.max(0, Math.min(1, c.tension));

    // layer levels: only a moved target is re-scheduled
    for (const id of Object.keys(layers) as LayerId[]) {
      const def: LayerDef = LAYERS[id];
      let lvl: number;
      if (id === 'throb') lvl = c.inbound && !c.eliminated ? 1 : 0;
      else if (!def.lv) lvl = 1;
      else {
        lvl = def.lv[st];
        if (id === 'pulse') lvl = (c.inbound ? Math.max(lvl, 0.6) * 1.4 : lvl) * (0.6 + 0.8 * tension);
        if (id === 'minor2' && st >= STAGE.watch && st <= STAGE.final) lvl += 0.3 * tension;
        if (crit) {
          if (id === 'pulse') lvl = 1.15 * (0.8 + 0.4 * tension);
          else if (id === 'minor2' || id === 'tritone') lvl = Math.max(lvl, 0.85);
          else if (id === 'fifth' || id === 'choir' || id === 'shimmer' || id === 'shepard' || id === 'piano') lvl *= 0.5;
        }
        if (c.eliminated) lvl = id === 'wind' ? 1 : id === 'geiger' ? 0.4 : id === 'drone' ? 0.25 : 0;
      }
      const l = layers[id];
      const target = def.base * lvl;
      if (Math.abs(target - l.last) > 1e-4) {
        ramp(l.fader.gain, target, id === 'throb' ? 0.5 : 3, t);
        l.last = target;
      }
    }
    g.droneLp.frequency.setTargetAtTime(150 + 260 * tension + (c.inbound ? 120 : 0), t, 2);
    const vowel = (Math.sin((t / 34) * Math.PI * 2) + 1) / 2;
    for (const bank of g.formants) bank.forEach((b, i) => b.frequency.setTargetAtTime(FORMANTS[i][0] * (1 - vowel) + FORMANTS[i][1] * vowel, t, 0.5));

    // the endless rise: one octave every 30 s, each sine fading in at the bottom and out at the top
    const p = (t / 30) % 1;
    for (const [i, s] of g.shepard.entries()) {
      const x = (i + p) % 7;
      const f = 40 * 2 ** x;
      if (x < s.last) s.o.frequency.setValueAtTime(f, t);
      else s.o.frequency.setTargetAtTime(f, t, 0.03);
      s.last = x;
      s.g.gain.setTargetAtTime(Math.exp(-((x - 3.5) ** 2) / (2 * 1.3 ** 2)) * 0.5, t, 0.05);
    }

    this.piano(g, t, ahead, crit);
    this.heartbeat(t, ahead, crit, tension);

    // Geiger: a Poisson process, with the odd burst
    const rate = c.eliminated ? 1.2 : GEIGER[st] * (0.5 + 1.5 * tension);
    if (rate > 0) {
      if (this.next.geiger < t) this.next.geiger = t + 0.05;
      while (this.next.geiger <= ahead) {
        this.geigerClick(g, this.next.geiger);
        this.next.geiger += (-Math.log(1 - Math.random()) / rate) * (Math.random() < 0.15 ? 0.15 : 1);
      }
    }
    // radio crackle and morse fragments
    if (this.next.crackle <= ahead) {
      const at = Math.max(this.next.crackle, t + 0.02);
      this.noiseBurst(g, at, rand(0.05, 0.5), rand(0.2, 1), g.radioBp);
      this.next.crackle = at + rand(0.3, 2.5);
    }
    if (this.next.morse <= ahead) {
      this.morse(g, Math.max(this.next.morse, t + 0.05));
      this.next.morse = t + rand(16, 38);
    }
  }

  private piano(g: Graph, t: number, ahead: number, crit: boolean): void {
    const st = this.cue.stage;
    if (this.cue.eliminated) return;
    const gone = new Set(LOST_ORDER.slice(0, this.lost));
    if (crit) {
      // stuck: the two lowest notes left, over and over
      if (this.next.phrase > ahead) return;
      const notes = motifAt(st);
      const keep = notes
        .map((_, i) => i)
        .filter((i) => !gone.has(i))
        .sort((a, b) => notes[a] - notes[b])
        .slice(0, 2)
        .reverse();
      let at = Math.max(this.next.phrase, t + 0.05);
      for (const i of keep) {
        this.pianoNote(g, at, notes[i], 0.6);
        at += rand(1.1, 1.6);
      }
      this.next.phrase = at + rand(5, 9);
      return;
    }
    if (st >= STAGE.watch && st <= STAGE.final && this.next.phrase <= ahead) {
      const notes = motifAt(st);
      const slow = st === STAGE.final ? 2.7 : 1.8;
      let at = Math.max(this.next.phrase, t + 0.05);
      const order = Math.random() < 0.25 ? [...notes.keys()].reverse() : [...notes.keys()];
      order.slice(Math.random() < 0.3 ? 2 : 0).forEach((i, k) => {
        if (st === STAGE.final && k % 2) return; // final: every other note
        if (!gone.has(i)) this.pianoNote(g, at, notes[i], 0.75 - k * 0.05);
        at += slow * rand(0.85, 1.15);
      });
      const [a, b] = PHRASE_GAP[st];
      this.next.phrase = at + rand(a, b);
    } else if (st === STAGE.lobby && this.next.single <= ahead) {
      this.pianoNote(g, Math.max(this.next.single, t + 0.05), [62, 69, 74, 77][Math.floor(Math.random() * 4)], 0.5);
      this.next.single = t + rand(9, 16);
    }
  }

  private heartbeat(t: number, ahead: number, crit: boolean, tension: number): void {
    const g = this.g!;
    const c = this.cue;
    if (c.eliminated) return;
    if (crit) {
      // arrhythmia: dropped beats, early weak ones with a pause after, flutters, uneven lub-dubs
      if (this.next.beat < t) this.next.beat = t + 0.05;
      while (this.next.beat <= ahead) {
        const base = 60 / (76 + 22 * tension);
        const r = Math.random();
        let next: number;
        if (r < 0.12) {
          next = base * 1.9;
          this.strongBeat = true;
        } else if (r < 0.3) {
          this.thump(g, this.next.beat, 0.45);
          next = base * 1.55;
        } else if (r < 0.38) {
          for (let k = 0; k < 3; k++) this.thump(g, this.next.beat + k * 0.19, 0.55 - k * 0.12);
          next = base * 1.4;
        } else {
          const v = this.strongBeat ? 1.35 : 1;
          this.strongBeat = false;
          this.thump(g, this.next.beat, v);
          this.thump(g, this.next.beat + rand(0.2, 0.34), 0.5 * v);
          next = base * rand(0.86, 1.14);
        }
        this.next.beat += next;
      }
      return;
    }
    const st = c.stage;
    const live = st >= STAGE.release && st <= STAGE.final;
    if (!live && !c.inbound) return;
    const bpm = (live ? BPM[st - STAGE.release] : 50) + tension * 14 + (c.inbound ? 12 : 0);
    const lubdub = st >= STAGE.final || tension > 0.6 || c.inbound;
    if (this.next.beat < t) this.next.beat = t + 0.05;
    while (this.next.beat <= ahead) {
      this.thump(g, this.next.beat, 1);
      if (lubdub) this.thump(g, this.next.beat + 0.27, 0.55);
      this.next.beat += 60 / bpm;
    }
  }

  // ------------------------------------------------------------------ one-shot voices

  /** A distant piano: a few sine partials (one detuned for the unison beat) under a soft low-pass. */
  private pianoNote(g: Graph, t: number, m: number, vel: number): void {
    const { ctx } = g;
    const f = midi(m);
    const bus = gain(ctx, vel);
    bus.connect(filt(ctx, 'lowpass', 1800 + vel * 2200)).connect(g.layers.piano.in);
    const long = m < 50 ? 1.8 : 1;
    for (const [r, a, d] of [[1, 1, 4.8], [1.0016, 0.35, 4.2], [2.003, 0.4, 2.6], [3.01, 0.18, 1.6], [4.04, 0.1, 1], [5.07, 0.05, 0.7]]) {
      const o = osc(ctx, 'sine', f * r);
      const env = gain(ctx, 0.0001);
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(a * 0.5, t + 0.006);
      env.gain.exponentialRampToValueAtTime(0.0001, t + d * long);
      o.connect(env).connect(bus);
      o.start(t);
      o.stop(t + d * long + 0.05);
    }
  }

  /** One heartbeat: a falling sub sine with its octave (so small speakers hear it). */
  private thump(g: Graph, t: number, v: number): void {
    for (const [mul, a] of [[1, 1], [2, 0.3]]) {
      const o = osc(g.ctx, 'sine', 78 * mul);
      const env = gain(g.ctx, 0.0001);
      o.frequency.setValueAtTime(78 * mul, t);
      o.frequency.exponentialRampToValueAtTime(36 * mul, t + 0.22);
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(v * a, t + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      o.connect(env).connect(g.layers.pulse.in);
      o.start(t);
      o.stop(t + 0.75);
    }
  }

  private noiseBurst(g: Graph, t: number, dur: number, vol: number, dest: AudioNode): void {
    const s = g.ctx.createBufferSource();
    s.buffer = g.noise;
    const env = gain(g.ctx, 0.0001);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(vol, t + Math.min(0.01, dur / 4));
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(env).connect(dest);
    s.start(t, Math.random() * 2, dur + 0.02);
  }

  private geigerClick(g: Graph, t: number): void {
    const s = g.ctx.createBufferSource();
    s.buffer = g.noise;
    s.connect(filt(g.ctx, 'highpass', 1800)).connect(gain(g.ctx, rand(0.3, 1))).connect(g.layers.geiger.in);
    s.start(t, Math.random() * 2, 0.0015 + Math.random() * 0.001);
  }

  /** A morse fragment on a drifting carrier, sometimes cut off mid-signal. */
  private morse(g: Graph, t0: number): void {
    const { ctx } = g;
    const f0 = rand(600, 690);
    const o = osc(ctx, 'sine', f0);
    const env = gain(ctx, 0);
    o.connect(env).connect(filt(ctx, 'bandpass', 650, 4)).connect(g.layers.radio.in);
    const unit = rand(0.055, 0.075);
    let text = MORSE_TEXT[Math.floor(Math.random() * MORSE_TEXT.length)];
    if (Math.random() < 0.4) text = text.slice(0, Math.max(1, Math.floor(text.length * rand(0.3, 0.8))));
    let t = t0;
    for (const ch of text) {
      if (ch === ' ') {
        t += unit * 4;
        continue;
      }
      for (const sym of MORSE[ch] ?? '') {
        const len = sym === '.' ? unit : unit * 3;
        env.gain.setTargetAtTime(1.6, t, 0.004);
        env.gain.setTargetAtTime(0, t + len, 0.004);
        t += len + unit;
      }
      t += unit * 2;
    }
    o.frequency.setValueAtTime(f0, t0);
    o.frequency.linearRampToValueAtTime(f0 + rand(-15, 15), t);
    o.start(t0);
    o.stop(t + 0.2);
  }

  /** Weapons free: an air-raid siren far away — two slow cycles, heavily reverberated. */
  private siren(g: Graph): void {
    const { ctx } = g;
    const t = ctx.currentTime + 0.05;
    const lp = filt(ctx, 'lowpass', 1000);
    const env = gain(ctx, 0.0001);
    const pan = ctx.createStereoPanner();
    pan.pan.setValueAtTime(rand(-0.6, 0.6), t);
    pan.pan.linearRampToValueAtTime(rand(-0.6, 0.6), t + 28);
    lp.connect(env).connect(pan).connect(g.layers.siren.in);
    for (const d of [-14, 0, 12]) {
      const o = osc(ctx, d ? 'sawtooth' : 'triangle', 150, d);
      const f = o.frequency;
      f.setValueAtTime(150, t);
      for (let c = 0; c < 2; c++) {
        const b = t + c * 13;
        f.exponentialRampToValueAtTime(540, b + 4.5);
        f.setValueAtTime(540, b + 7.5);
        f.exponentialRampToValueAtTime(140, b + 13);
      }
      o.connect(gain(ctx, 0.33)).connect(lp);
      o.start(t);
      o.stop(t + 28);
    }
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(1, t + 3.5);
    env.gain.setValueAtTime(1, t + 18);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 27);
  }
}

// ------------------------------------------------------------------ node helpers

function osc(ctx: AudioContext, type: OscillatorType, f: number, detune = 0): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  o.detune.value = detune;
  return o;
}

function gain(ctx: AudioContext, v: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = v;
  return g;
}

function filt(ctx: AudioContext, type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  return b;
}

/** Glide `param` from where it is now to `target` (time constant `tau`). */
function ramp(param: AudioParam, target: number, tau: number, at: number): void {
  param.cancelScheduledValues(at);
  param.setValueAtTime(param.value, at);
  param.setTargetAtTime(target, at, tau);
}

function noise(ctx: AudioContext, seconds: number): AudioBuffer {
  const b = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

/** A large dark room: stereo noise with a 30 ms gap, decaying, losing its highs first. */
function impulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const b = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    let y = 0;
    for (let i = 0; i < len; i++) {
      const k = i / len;
      y += (0.9 - 0.75 * k) * (Math.random() * 2 - 1 - y);
      d[i] = i < ctx.sampleRate * 0.03 ? 0 : y * (1 - k) ** 3.2;
    }
  }
  return b;
}
